import { io, Socket } from "socket.io-client";
import { getAccessToken } from "services/token.service";

export interface NotificationRealtimePayload {
  notificationId: string;
  receiverUserId: string;
  senderUserId: string | null;
  title: string;
  body: string;
  type: string;
  orderId: string | null;
  containerId: string | null;
  routeId: string | null;
  transportCostId: string | null;
  status: "read" | "unread";
  readAt: string | null;
  createdAt: string;
}

export interface NotificationRealtimeEnvelope {
  deliveryId: string;
  notification: NotificationRealtimePayload;
}

interface NotificationDeliveredPayload {
  deliveryId: string;
}

class NotificationSocketService {
  private socket: Socket | null = null;
  private currentToken: string | null = null;
  private readonly listeners = new Set<
    (payload: NotificationRealtimeEnvelope) => void
  >();
  private readonly notificationEvent = "notification.new";
  private readonly handleIncomingNotification = (
    payload: NotificationRealtimeEnvelope,
  ): void => {
    this.acknowledgeDelivered(payload.deliveryId);

    this.listeners.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error("[NotificationSocket] Listener failed:", error);
      }
    });
  };

  connect(token?: string): Socket | null {
    const accessToken = token || getAccessToken();

    if (!accessToken) {
      this.disconnect();
      return null;
    }

    if (this.socket && this.currentToken === accessToken) {
      return this.socket;
    }

    this.disconnect();

    const configuredBaseUrl =
      process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL;

    if (!configuredBaseUrl && process.env.NODE_ENV === "production") {
      console.error(
        "[NotificationSocket] Missing REACT_APP_API_URL or REACT_APP_BASE_URL",
      );
      return null;
    }

    const baseUrl = configuredBaseUrl || "http://localhost:3000";

    this.socket = io(`${baseUrl}/notifications`, {
      transports: ["websocket"],
      auth: {
        token: accessToken,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });
    this.currentToken = accessToken;
    this.socket.on(this.notificationEvent, this.handleIncomingNotification);

    return this.socket;
  }

  disconnect(): void {
    if (!this.socket) {
      this.currentToken = null;
      return;
    }

    this.socket.off(this.notificationEvent, this.handleIncomingNotification);
    this.socket.disconnect();
    this.socket = null;
    this.currentToken = null;
  }

  onNotification(
    callback: (payload: NotificationRealtimeEnvelope) => void,
  ): void {
    this.listeners.add(callback);
  }

  offNotification(
    callback: (payload: NotificationRealtimeEnvelope) => void,
  ): void {
    this.listeners.delete(callback);
  }

  acknowledgeDelivered(deliveryId: string): void {
    const payload: NotificationDeliveredPayload = { deliveryId };
    this.socket?.emit("notification.delivered", payload);
  }
}

export const notificationSocketService = new NotificationSocketService();
